import { cn } from '../../../../lib/utils';
import { ReactNode, isValidElement } from 'react';
import Switch from '../../../primitives/controls/Switch/Switch';
import TooltipTriggerIcon, {
  TooltipIconType,
} from '../../../primitives/feedback-and-status/tooltip/TooltipTriggerIcon';
import { TooltipProps } from '../../../primitives/feedback-and-status/tooltip/Tooltip';

export type GeneralHeaderSize = 'sm' | 'md' | 'lg';

export interface GeneralHeaderProps {
  className?: string;
  leadingIcon?: ReactNode;
  title: string;
  labels?: ReactNode;
  description?: string;
  trailingNode?: ReactNode;
  descriptionClassName?: string;
  size?: GeneralHeaderSize;
  responsive?: boolean;
  /** Tooltip 아이콘 표시 여부 */
  isShowTooltipIcon?: boolean;
  /** Tooltip 아이콘 타입 */
  tooltipIconType?: TooltipIconType;
  /** Tooltip 텍스트 */
  tooltipText?: string;
  /** 추가 Tooltip Props */
  tooltipProps?: Partial<Omit<TooltipProps, 'textNode' | 'children'>>;
}

// Switch 컴포넌트인지 확인하는 헬퍼 함수
function isSwitchComponent(node: ReactNode): boolean {
  if (!isValidElement(node)) return false;
  // Switch 컴포넌트의 타입을 확인
  return node.type === Switch;
}

export default function GeneralHeader({
  className = '',
  leadingIcon,
  title,
  labels,
  description,
  trailingNode,
  descriptionClassName,
  size = 'lg',
  responsive = false,
  isShowTooltipIcon = false,
  tooltipIconType = 'help',
  tooltipText,
  tooltipProps,
}: GeneralHeaderProps) {
  // 제목 색상 클래스 (기본 상태)
  const titleColorClassName = 'text-sys-cont-neutral-default';
  // 아이콘 색상 클래스 (제목과 동일)
  const iconColorClassName = '[&_path]:fill-sys-cont-neutral-default';
  // 사이즈별 스타일 설정
  const sizeStyles = {
    sm: {
      icon: 'size-4', // 16px
      title: 'text-title-xsmall',
      description: 'text-body-xsmall',
    },
    md: {
      icon: 'size-5', // 20px
      title: 'text-title-small',
      description: 'text-body-small',
    },
    lg: {
      icon: 'size-5', // 20px (기존과 동일)
      title: 'text-title-medium',
      description: 'text-body-medium',
    },
  };

  const currentSize = sizeStyles[size];
  const isSwitch = trailingNode ? isSwitchComponent(trailingNode) : false;

  // responsive에 따른 padding 클래스
  const paddingClass = responsive ? 'screens-layout-side' : 'px-md';

  // Switch 타입인 경우 기존 레이아웃 유지
  if (isSwitch) {
    return (
      <div
        className={cn(
          'general-header',
          'bg-sys-bg-surface py-xs',
          paddingClass,
          className,
        )}
      >
        <div className="flex w-full items-center justify-between gap-2xs">
          <div className="flex flex-col">
            <div className="general-header-left flex items-center gap-4xs">
              {leadingIcon && (
                <div
                  className={cn(
                    'general-header-leading-icon flex items-center [&_path]:fill-sys-cont-neutral-default',
                    currentSize.icon,
                  )}
                >
                  {leadingIcon}
                </div>
              )}
              <h3
                className={cn(
                  'general-header-title flex items-center',
                  currentSize.title,
                  titleColorClassName,
                )}
              >
                {title}
              </h3>
              {isShowTooltipIcon && tooltipText && (
                <TooltipTriggerIcon
                  iconType={tooltipIconType}
                  tooltipText={tooltipText}
                  iconColorClassName={iconColorClassName}
                  tooltipProps={tooltipProps}
                />
              )}
              {labels && (
                <div className="general-header-labels flex items-center">
                  {labels}
                </div>
              )}
            </div>
            {description && (
              <p
                className={cn(
                  'general-header-description line-clamp-2',
                  currentSize.description,
                  descriptionClassName,
                  'text-sys-cont-neutral-light',
                )}
              >
                {description}
              </p>
            )}
          </div>
          {trailingNode && (
            <div className="general-header-trailing flex items-center">
              {trailingNode}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Switch가 아닌 경우 새로운 레이아웃
  return (
    <div
      className={cn(
        'general-header',
        'bg-sys-bg-surface py-xs',
        paddingClass,
        className,
      )}
    >
      <div className="flex w-full flex-col gap-2xs">
        {/* 제목과 버튼을 수평으로 배치 */}
        <div className="flex w-full items-center justify-between gap-2xs">
          <div className="general-header-left flex items-center gap-4xs">
            {leadingIcon && (
              <div
                className={cn(
                  'general-header-leading-icon flex items-center [&_path]:fill-sys-cont-neutral-default',
                  currentSize.icon,
                )}
              >
                {leadingIcon}
              </div>
            )}
            <h3
              className={cn(
                'general-header-title flex items-center',
                currentSize.title,
                titleColorClassName,
              )}
            >
              {title}
            </h3>
            {isShowTooltipIcon && tooltipText && (
              <TooltipTriggerIcon
                iconType={tooltipIconType}
                tooltipText={tooltipText}
                iconColorClassName={iconColorClassName}
                tooltipProps={tooltipProps}
              />
            )}
            {labels && (
              <div className="general-header-labels flex items-center">
                {labels}
              </div>
            )}
          </div>
          {trailingNode && (
            <div className="general-header-trailing flex items-center">
              {trailingNode}
            </div>
          )}
        </div>
        {/* 설명을 아래에 수직으로 배치 */}
        {description && (
          <p
            className={cn(
              'general-header-description line-clamp-2',
              currentSize.description,
              descriptionClassName,
              'text-sys-cont-neutral-light',
            )}
          >
            {description}
          </p>
        )}
      </div>
    </div>
  );
}
